import React from 'react';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import Tooltip from 'components/Tooltip';
import Typography from 'kingpin/atoms/Typography';

import useReportScheduleRuns from './useReportScheduleRuns';

const Wrapper = styled.div`
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid var(--color-border-primary);
`;

const RunRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
`;

const STATUS_COLOR: Record<ReportSchedules.RunStatus, string> = {
  succeeded: 'var(--color-badge-green-text)',
  failed: 'var(--color-badge-red-text)',
  'dead-lettered': 'var(--color-badge-red-text)',
  skipped: 'var(--color-icon-secondary)',
  enqueued: 'var(--color-icon-secondary)',
  leased: 'var(--color-icon-secondary)',
  rendering: 'var(--color-icon-secondary)',
  rendered: 'var(--color-icon-secondary)',
  'render-failed': 'var(--color-badge-red-text)',
};

const STATUS_LABEL: Record<ReportSchedules.RunStatus, string> = {
  succeeded: 'Sent',
  failed: 'Failed (retrying)',
  'dead-lettered': 'Failed',
  skipped: 'Skipped',
  enqueued: 'Queued',
  leased: 'In progress',
  rendering: 'In progress',
  rendered: 'In progress',
  'render-failed': 'Failed (retrying)',
};

// A sent delivery that left something out, and what
const getMissing = (run: ReportSchedules.Run): string[] =>
  run.status === 'succeeded' && run.isPartial
    ? [
        ...(run.incompleteGadgets ?? []),
        ...(run.failedGadgets ?? []),
        ...(run.missingCsvs ?? []).map((name) => `${name} (CSV)`),
      ]
    : [];

const formatSlot = (iso: string): string => {
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toFormat('d LLL yyyy, HH:mm') : iso;
};

const RunHistory = ({
  scheduleId,
  nextRunAt,
}: {
  scheduleId: string;
  nextRunAt: string;
}) => {
  const { runs, isLoading } = useReportScheduleRuns({ scheduleId });

  return (
    <Wrapper>
      <Typography.Body type="Body 13 Bold">Delivery history</Typography.Body>

      <RunRow>
        <Typography.Body type="Body 12" color="var(--color-icon-secondary)">
          Next run
        </Typography.Body>
        <Typography.Body type="Body 12">
          {formatSlot(nextRunAt)}
        </Typography.Body>
      </RunRow>

      {!isLoading && runs.length === 0 && (
        <Typography.Body type="Body 12" color="var(--color-icon-secondary)">
          No runs yet
        </Typography.Body>
      )}

      {runs.map((run) => {
        const missing = getMissing(run);
        return (
          <RunRow key={run.id}>
            <Typography.Body type="Body 12">
              {formatSlot(run.slot)}
            </Typography.Body>
            {missing.length > 0 ? (
              <Tooltip
                content={`Sent without: ${missing.join(', ')}`}
                testId={`run-partial-${run.id}`}
              >
                <Typography.Body
                  type="Body 12"
                  color="var(--color-badge-yellow-text)"
                >
                  Sent (partial)
                </Typography.Body>
              </Tooltip>
            ) : (
              <Typography.Body type="Body 12" color={STATUS_COLOR[run.status]}>
                {STATUS_LABEL[run.status]}
              </Typography.Body>
            )}
          </RunRow>
        );
      })}
    </Wrapper>
  );
};

export default RunHistory;
